import {
  getCsvBatchDiscoveryEvents,
  getPersonalizedLandingEventFeed,
  getPublicLandingEventFeed,
} from '../util/api';

const LOAD = 'app/landingEvents/LOAD';
const SUCCESS = 'app/landingEvents/SUCCESS';
const FAILURE = 'app/landingEvents/FAILURE';
const PUBLIC_LOAD = 'app/landingEvents/PUBLIC_LOAD';
const PUBLIC_SUCCESS = 'app/landingEvents/PUBLIC_SUCCESS';
const PUBLIC_FAILURE = 'app/landingEvents/PUBLIC_FAILURE';
const PERSONALIZED_LOAD = 'app/landingEvents/PERSONALIZED_LOAD';
const PERSONALIZED_SUCCESS = 'app/landingEvents/PERSONALIZED_SUCCESS';
const PERSONALIZED_FAILURE = 'app/landingEvents/PERSONALIZED_FAILURE';
const TTL = 15 * 60 * 1000;

const initialState = {
  sections: {},
  loading: false,
  error: null,
  fetchedAt: 0,
  viewerKey: null,
  publicFeed: null,
  publicLoading: false,
  publicError: null,
  publicFetchedAt: 0,
  personalizedFeeds: {},
};
let inFlight = null;
let publicInFlight = null;
const personalizedInFlight = new Map();
const personalizedControllers = new Map();

export const cancelPersonalizedLandingEventFeed = viewerKey => () => {
  const keys = viewerKey ? [viewerKey] : [...personalizedControllers.keys()];
  keys.forEach(key => {
    personalizedControllers.get(key)?.abort();
    personalizedControllers.delete(key);
    personalizedInFlight.delete(key);
  });
};

export const fetchPublicLandingEventFeed = (options = {}) => (dispatch, getState) => {
  const cached = getState().landingEvents || initialState;
  if (!options.force && cached.publicFeed && Date.now() - cached.publicFetchedAt < TTL) {
    return Promise.resolve(cached.publicFeed);
  }
  if (publicInFlight && !options.force) return publicInFlight;

  dispatch({ type: PUBLIC_LOAD });
  publicInFlight = getPublicLandingEventFeed()
    .then(payload => {
      dispatch({ type: PUBLIC_SUCCESS, payload });
      return payload;
    })
    .catch(error => {
      dispatch({ type: PUBLIC_FAILURE, error });
      if (cached.publicFeed) return cached.publicFeed;
      throw error;
    })
    .finally(() => {
      publicInFlight = null;
    });
  return publicInFlight;
};

export const fetchPersonalizedLandingEventFeed = (viewerKey, options = {}) => (
  dispatch,
  getState
) => {
  if (!viewerKey) return Promise.reject(new Error('A viewer key is required.'));
  const cached = getState().landingEvents || initialState;
  const cachedFeed = cached.personalizedFeeds?.[viewerKey];
  if (!options.force && cachedFeed?.data && Date.now() - cachedFeed.fetchedAt < TTL) {
    return Promise.resolve(cachedFeed.data);
  }
  if (personalizedInFlight.has(viewerKey) && !options.force) {
    return personalizedInFlight.get(viewerKey);
  }

  if (options.force) {
    personalizedControllers.get(viewerKey)?.abort();
    personalizedControllers.delete(viewerKey);
    personalizedInFlight.delete(viewerKey);
  }

  dispatch({ type: PERSONALIZED_LOAD, viewerKey });
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  if (controller) personalizedControllers.set(viewerKey, controller);
  const request = getPersonalizedLandingEventFeed({ signal: controller?.signal })
    .then(payload => {
      dispatch({ type: PERSONALIZED_SUCCESS, payload, viewerKey });
      return payload;
    })
    .catch(error => {
      if (error?.name === 'AbortError') throw error;
      dispatch({ type: PERSONALIZED_FAILURE, error, viewerKey });
      if (cachedFeed?.data) return cachedFeed.data;
      throw error;
    })
    .finally(() => {
      personalizedInFlight.delete(viewerKey);
      if (personalizedControllers.get(viewerKey) === controller) {
        personalizedControllers.delete(viewerKey);
      }
    });
  personalizedInFlight.set(viewerKey, request);
  return request;
};

export const fetchLandingEventsBatch = (sections, viewerKey = 'guest') => (dispatch, getState) => {
  const cached = getState().landingEvents;
  const cachedHasEvents = Object.values(cached?.sections || {}).some(
    section => Array.isArray(section?.events) && section.events.length > 0
  );
  if (cached?.viewerKey === viewerKey && cachedHasEvents && Date.now() - cached.fetchedAt < TTL) {
    return Promise.resolve(cached.sections);
  }
  if (inFlight) return inFlight;
  dispatch({ type: LOAD });
  inFlight = getCsvBatchDiscoveryEvents(sections)
    .then(response => {
      const data = response?.sections || {};
      dispatch({ type: SUCCESS, payload: data, viewerKey });
      return data;
    })
    .catch(error => {
      dispatch({ type: FAILURE, error });
      throw error;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

export default (state = initialState, action = {}) => {
  switch (action.type) {
    case PUBLIC_LOAD:
      return { ...state, publicLoading: true, publicError: null };
    case PUBLIC_SUCCESS:
      return {
        ...state,
        publicFeed: action.payload,
        publicLoading: false,
        publicError: null,
        publicFetchedAt: Date.now(),
      };
    case PUBLIC_FAILURE:
      return { ...state, publicLoading: false, publicError: action.error };
    case PERSONALIZED_LOAD:
      return {
        ...state,
        personalizedFeeds: {
          ...state.personalizedFeeds,
          [action.viewerKey]: {
            ...(state.personalizedFeeds[action.viewerKey] || {}),
            loading: true,
            error: null,
          },
        },
      };
    case PERSONALIZED_SUCCESS:
      return {
        ...state,
        personalizedFeeds: {
          ...state.personalizedFeeds,
          [action.viewerKey]: {
            data: action.payload,
            loading: false,
            error: null,
            fetchedAt: Date.now(),
          },
        },
      };
    case PERSONALIZED_FAILURE:
      return {
        ...state,
        personalizedFeeds: {
          ...state.personalizedFeeds,
          [action.viewerKey]: {
            ...(state.personalizedFeeds[action.viewerKey] || {}),
            loading: false,
            error: action.error,
          },
        },
      };
    case LOAD:
      return { ...state, loading: true, error: null };
    case SUCCESS:
      return {
        ...state,
        sections: action.payload,
        loading: false,
        error: null,
        fetchedAt: Date.now(),
        viewerKey: action.viewerKey,
      };
    case FAILURE:
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
};
